import React, { useEffect, useMemo, useState } from "react";
import { Search, Plus, RotateCcw, ChevronLeft, ChevronRight, Info, X } from "lucide-react";
import "./TracksPage.css";
import { Link, useSearchParams } from "react-router-dom";
import { tracksApi } from "../../api/client";
import { getApiErrorMessage } from "../../api/http";
import { useAuth } from "../../context/AuthContext";
import { resolveMediaUrl } from "../../utils/media";
import { clearActiveTrackId, setActiveTrackId } from "../../utils/session";
import { getTrackInstructorNames, isTrackOwnedByUser } from "../../utils/track";

const STATUS_LABELS = {
  approved: "Enrolled",
  pending: "Pending Approval",
  rejected: "Rejected",
  not_enrolled: "Not Enrolled",
  owned: "My Track",
  admin: "Admin Access",
};

const STATUS_CLASS = {
  approved: "enrolled",
  pending: "pending-approval",
  rejected: "not-enrolled",
  not_enrolled: "not-enrolled",
  owned: "enrolled",
  admin: "enrolled",
};

const gradients = ["grad-blue", "grad-green", "grad-grey", "grad-dark", "grad-gold", "grad-teal"];

const TrackCard = ({
  track,
  userRole,
  userId,
  enrollmentStatus,
  onEnroll,
  onUnenroll,
  roleReady,
  isFocused,
  onViewDetails,
}) => {
  const isOwner = isTrackOwnedByUser(track, userId);

  let normalizedStatus = enrollmentStatus || "not_enrolled";
  if (userRole === "instructor") normalizedStatus = isOwner ? "owned" : "not_enrolled";
  if (userRole === "super_admin") normalizedStatus = "admin";

  const statusLabel = STATUS_LABELS[normalizedStatus] || STATUS_LABELS.not_enrolled;
  const statusClass = STATUS_CLASS[normalizedStatus] || STATUS_CLASS.not_enrolled;
  const bannerUrl = resolveMediaUrl(track.bannerUrl);
  const imageUrl = resolveMediaUrl(track.imageUrl);
  const headerClassName = bannerUrl
    ? "card-header-img custom-banner"
    : `card-header-img ${track.gradient}`;
  const headerStyle = bannerUrl
    ? {
        backgroundImage: `linear-gradient(0deg, rgba(15, 23, 42, 0.3), rgba(15, 23, 42, 0.3)), url(${bannerUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : undefined;
  const avatarStyle = imageUrl
    ? {
        backgroundImage: `url(${imageUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : undefined;

  const onSelectTrack = () => {
    setActiveTrackId(track._id);
  };

  const renderButton = () => {
    if (!roleReady) {
      return (
        <button className="btn btn-light rounded-pill w-100 text-muted py-2" disabled>
          Loading...
        </button>
      );
    }

    if (userRole === "super_admin") {
      return (
        <Link
          to="/dashboard"
          onClick={onSelectTrack}
          className="btn btn-outline-secondary rounded-pill w-100 fw-bold py-2"
        >
          Go to Dashboard
        </Link>
      );
    }

    if (userRole === "instructor") {
      if (!isOwner) {
        return (
          <button className="btn btn-light rounded-pill w-100 text-muted py-2" disabled>
            Not Assigned
          </button>
        );
      }
      return (
        <Link
          to="/dashboard"
          onClick={onSelectTrack}
          className="btn btn-outline-secondary rounded-pill w-100 fw-bold py-2"
        >
          Go to Dashboard
        </Link>
      );
    }

    if (normalizedStatus === "approved") {
      return (
        <div className="d-grid gap-2">
          <Link
            to="/dashboard"
            onClick={onSelectTrack}
            className="btn btn-outline-secondary rounded-pill w-100 fw-bold py-2"
          >
            Go to Dashboard
          </Link>
          <button
            onClick={() => onUnenroll(track._id, true)}
            className="btn btn-outline-danger rounded-pill w-100 fw-bold py-2"
          >
            Cancel Enrollment
          </button>
        </div>
      );
    }

    if (normalizedStatus === "pending") {
      return (
        <div className="d-grid gap-2">
          <button className="btn btn-light rounded-pill w-100 text-muted py-2" disabled>
            Request Pending
          </button>
          <button
            onClick={() => onUnenroll(track._id, false)}
            className="btn btn-outline-danger rounded-pill w-100 fw-bold py-2"
          >
            Cancel Request
          </button>
        </div>
      );
    }

    return (
      <button onClick={() => onEnroll(track._id)} className="btn btn-primary-ieee fw-bold rounded-pill w-100 py-2">
        Request Enrollment
      </button>
    );
  };

  return (
    <div className="col-12 col-md-6 col-lg-4 mb-4">
      <div
        id={`track-card-${track._id}`}
        className={`card track-card border-0 shadow-sm h-100 ${isFocused ? "track-card-focused" : ""}`}
      >
        <div className={headerClassName} style={headerStyle}>
          <span className={`status-badge ${statusClass}`}>
            {statusLabel}
          </span>
        </div>
        <div className="card-body p-4">
          <h5 className="fw-bold mb-1">{track.name}</h5>
          <p className="text-muted small mb-3">{track.status?.toUpperCase() || "OPEN"} � Track</p>
          <p className="text-secondary small mb-4 track-description-preview">
            {track.description || "Master the curriculum with hands-on projects and mentorship."}
          </p>

          <div className="d-flex align-items-center mb-4">
            <div className="instructor-avatar me-3" style={avatarStyle}></div>
            <div>
              <p className="text-muted extra-small mb-0">Instructors</p>
              <p className="fw-bold mb-0 small">{getTrackInstructorNames(track).join(", ") || "Not assigned yet"}</p>
            </div>
          </div>

          <div className="track-card-actions mt-auto d-grid gap-2">
            <button type="button" className="btn btn-outline-light rounded-pill fw-bold py-2" onClick={() => onViewDetails(track)}>
              <Info size={16} /> View More Details
            </button>
            {renderButton()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function TracksPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const userRole = user?.role || "";
  const userId = user?.id || user?._id;
  const roleReady = Boolean(userRole);
  const focusedTrackId = searchParams.get("trackId") || "";

  const [tracks, setTracks] = useState([]);
  const [enrollmentByTrack, setEnrollmentByTrack] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [selectedTrack, setSelectedTrack] = useState(null);

  const loadTracks = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await tracksApi.list();
      const formatted = (data?.tracks || []).map((track, idx) => ({
        ...track,
        gradient: gradients[idx % gradients.length],
      }));
      setTracks(formatted);

      const focusedTrack = focusedTrackId
        ? formatted.find((track) => `${track._id}` === `${focusedTrackId}`)
        : null;
      if (focusedTrack) {
        setActiveTrackId(focusedTrack._id);
      }

      if (userRole === "student") {
        const statuses = await Promise.all(
          formatted.map(async (track) => {
            try {
              const response = await tracksApi.enrollmentStatus(track._id);
              return [track._id, response.data?.status || "not_enrolled"];
            } catch {
              return [track._id, "not_enrolled"];
            }
          })
        );

        const statusMap = Object.fromEntries(statuses);
        setEnrollmentByTrack(statusMap);

        const approvedTrack = formatted.find((track) => statusMap[track._id] === "approved" && track.status !== "closed");
        if (!focusedTrack && approvedTrack) {
          setActiveTrackId(approvedTrack._id);
        } else if (!focusedTrack) {
          clearActiveTrackId();
        }
      } else if (userRole === "instructor") {
        const ownedTrack = formatted.find((track) => isTrackOwnedByUser(track, userId));
        if (!focusedTrack && ownedTrack) {
          setActiveTrackId(ownedTrack._id);
        } else if (!focusedTrack) {
          clearActiveTrackId();
        }
      } else if (!focusedTrack && userRole === "super_admin" && formatted.length > 0) {
        setActiveTrackId(formatted[0]._id);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to load tracks"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!roleReady) return;
    loadTracks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleReady, userRole, userId, focusedTrackId]);

  useEffect(() => {
    if (!focusedTrackId || !tracks.length) return;
    const focusedTrackExists = tracks.some((track) => `${track._id}` === `${focusedTrackId}`);
    if (!focusedTrackExists) return;

    const raf = requestAnimationFrame(() => {
      const card = document.getElementById(`track-card-${focusedTrackId}`);
      card?.scrollIntoView({ behavior: "smooth", block: "center" });
    });

    return () => cancelAnimationFrame(raf);
  }, [focusedTrackId, tracks]);

  const handleEnroll = async (trackId) => {
    if (userRole !== "student") return;
    setActionMessage("");
    setError("");
    try {
      const { data } = await tracksApi.enroll(trackId);
      setEnrollmentByTrack((prev) => ({ ...prev, [trackId]: data?.enrollment?.status || "pending" }));
      setActionMessage("Enrollment request sent successfully.");
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to send enrollment request"));
    }
  };

  const handleUnenroll = async (trackId, isApproved) => {
    if (userRole !== "student") return;
    const confirmed = window.confirm(
      isApproved
        ? "Are you sure you want to cancel your enrollment in this track?"
        : "Cancel your pending enrollment request?"
    );
    if (!confirmed) return;

    setActionMessage("");
    setError("");
    try {
      await tracksApi.unenroll(trackId);
      setEnrollmentByTrack((prev) => {
        const next = { ...prev };
        delete next[trackId];
        return next;
      });
      setActionMessage(isApproved ? "Enrollment cancelled successfully." : "Enrollment request cancelled.");
      await loadTracks();
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to cancel enrollment"));
    }
  };

  const visibleTracks = useMemo(() => {
    if (!roleReady) return [];
    const openTracks = tracks.filter((track) => track.status !== "closed");
    if (!focusedTrackId) return openTracks;
    return openTracks.filter((track) => `${track._id}` === `${focusedTrackId}`);
  }, [focusedTrackId, roleReady, tracks]);

  const filteredTracks = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return visibleTracks;
    return visibleTracks.filter((track) => {
      return (
        track.name?.toLowerCase().includes(q) ||
        track.description?.toLowerCase().includes(q) ||
        getTrackInstructorNames(track).some((name) => name.toLowerCase().includes(q))
      );
    });
  }, [searchTerm, visibleTracks]);

  const selectedTrackBannerUrl = resolveMediaUrl(selectedTrack?.bannerUrl);
  const selectedTrackImageUrl = resolveMediaUrl(selectedTrack?.imageUrl);
  const selectedTrackInitials = useMemo(() => {
    const rawName = selectedTrack?.name || "Track";
    return rawName
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((segment) => segment[0]?.toUpperCase())
      .join("");
  }, [selectedTrack?.name]);

  return (
    <div className="bg-light-subtle min-vh-100 pb-5">
      <div className="container pt-5">
        <header className="d-flex justify-content-between align-items-end mb-5 flex-wrap gap-3">
          <div>
            <h1 className="fw-bold">Explore Tracks</h1>
            <p className="text-muted mb-0">
              Browse available academic tracks, manage your enrollments, and track progress.
            </p>
          </div>
          <button className="btn btn-primary-ieee  px-4 py-2 rounded-pill d-flex align-items-center gap-2" disabled>
            <Plus size={18} /> Request New Track
          </button>
        </header>

        <div className="row g-3 mb-4 align-items-center">
          <div className="col-md-11 position-relative">
            <Search className="search-icon" size={18} />
            <input
              type="text"
              className="form-control ps-5 py-2 rounded-pill search-input"
              placeholder="Search tracks by name, or instructors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="col-md-1 d-flex justify-content-end">
            <button onClick={loadTracks} className="btn btn-white rounded-circle shadow-sm border-0 p-2">
              <RotateCcw size={20} className="text-muted" />
            </button>
          </div>
        </div>

        {error ? <div className="alert alert-danger py-2">{error}</div> : null}
        {actionMessage ? <div className="alert alert-success py-2">{actionMessage}</div> : null}
        {loading ? <div className="alert alert-info py-2">Loading tracks...</div> : null}

        <div className="row">
          {filteredTracks.map((track) => (
            <TrackCard
              key={track._id}
              track={track}
              userRole={userRole}
              userId={userId}
              enrollmentStatus={enrollmentByTrack[track._id]}
              onEnroll={handleEnroll}
              onUnenroll={handleUnenroll}
              roleReady={roleReady}
              isFocused={`${track._id}` === `${focusedTrackId}`}
              onViewDetails={setSelectedTrack}
            />
          ))}
        </div>

        <div className="d-flex justify-content-between align-items-center mt-5 text-muted small flex-wrap gap-2">
          <span>
            Showing <b>{filteredTracks.length}</b> of <b>{visibleTracks.length}</b> tracks
          </span>
          <div className="d-flex gap-2">
            <button className="btn btn-white btn-sm rounded-pill px-3 shadow-sm border text-muted" disabled>
              <ChevronLeft size={16} /> Previous
            </button>
            <button className="btn btn-white btn-sm rounded-pill px-3 shadow-sm border text-muted" disabled>
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {selectedTrack ? (
        <div className="track-details-backdrop" onClick={() => setSelectedTrack(null)}>
          <article className="track-details-modal" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="track-details-close-btn" onClick={() => setSelectedTrack(null)} aria-label="Close">
              <X size={18} />
            </button>

            <header className="track-details-banner" style={selectedTrackBannerUrl ? { backgroundImage: `url(${selectedTrackBannerUrl})` } : undefined}>
              <span className={`status-badge ${STATUS_CLASS[selectedTrack.status] || "not-enrolled"}`}>
                {selectedTrack.status?.toUpperCase() || "OPEN"}
              </span>
            </header>

            <section className="track-details-content">
              <div className="track-details-top">
                <div className="track-details-avatar">
                  {selectedTrackImageUrl ? (
                    <img src={selectedTrackImageUrl} alt={selectedTrack.name} />
                  ) : (
                    <span>{selectedTrackInitials}</span>
                  )}
                </div>
                <div>
                  <h3>{selectedTrack.name}</h3>
                  <p>{selectedTrack.status?.toUpperCase() || "OPEN"} � Track</p>
                </div>
              </div>

              <div className="track-details-section">
                <h4>Description</h4>
                <p>{selectedTrack.description || "No description available."}</p>
              </div>

              <div className="track-details-section">
                <h4>Instructors</h4>
                <p>{getTrackInstructorNames(selectedTrack).join(", ") || "Not assigned yet"}</p>
              </div>
            </section>
          </article>
        </div>
      ) : null}
    </div>
  );
}
